/**
 * Main dashboard — overview cards + recent calls table + click to expand details.
 *
 * Intentionally simple: 1 page, no router. Anh sẽ dùng và feedback gì cần thêm.
 */

import { useEffect, useState } from 'react';
import type {
  BrandContextResponse,
  CallDetailResponse,
  CallRow,
  ConfigEndpoint,
  ModelConfigResponse,
  StatsResponse,
} from '../types.js';
import {
  getBrandContext,
  getCallDetail,
  getModelConfig,
  getStats,
  listCalls,
  setBrandContext,
  setModelConfig,
} from '../lib/api.js';
import type { UseAuthReturn } from '../hooks/useAuth.js';
import { AiSeesView, NoiseReviewSection } from './NoiseReview.js';
import { ReplySpeedSection } from './ReplySpeed.js';
import { ShortcutLockSection } from './ShortcutLock.js';

interface DashboardProps {
  auth: UseAuthReturn;
}

const ENDPOINT_LABELS: Record<string, string> = {
  'reply-polish': 'Reply (polish)',
  'reply-auto': 'Reply (auto)',
  'reply-polish-nail': 'Reply nail (polish)',
  'reply-auto-nail': 'Reply nail (auto)',
  translate: 'Translate VI→',
  'translate-inbound': 'Translate inbound (OCR→VI)',
  'vision-extract': 'Vision OCR',
  'comment-reply': 'Trả lời comment',
  'comment-reply-polish': 'Comment · sửa theo ý anh',
};

const CONFIG_ENDPOINT_LABELS: Record<ConfigEndpoint, { title: string; hint: string }> = {
  reply: {
    title: 'Reply (auto + long)',
    hint: 'AI tự trả lời từ kho kiến thức (để trống nháp). Đổi tự do giữa Anthropic / OpenAI / Gemini.',
  },
  reply_polish: {
    title: 'Reply polish (có nháp)',
    hint: 'Làm đẹp câu nháp của anh (kèm bảng giá + 5 câu mẫu liên quan). gpt-5.4-mini đã thử: hay trả tiếng Việt, cộc — nên giữ gpt-5.1.',
  },
  comment_reply: {
    title: 'Trả lời comment (TikTok/IG)',
    hint: 'Ảnh chụp phần comment → tách từng comment + reply công khai. Cần model đọc được ảnh; ưu tiên OpenAI vì key Gemini/Anthropic có thể bị chặn khi anh ở VN.',
  },
  comment_polish: {
    title: 'Comment · sửa theo ý anh',
    hint: 'Viết lại 1 reply comment theo ghi chú của anh (nút ✏️ trên trang kết quả). Chỉ chữ, ngắn.',
  },
  translate_outbound: {
    title: 'Translate VI →',
    hint: 'Dịch tin nhân viên gửi khách. Hiện chỉ swap model trong OpenAI.',
  },
  translate_inbound: {
    title: 'Translate OCR → VI',
    hint: 'Đọc chat khách qua iPhone Shortcut. Hiện chỉ swap model trong Gemini.',
  },
};

function fmtModel(modelId: string): string {
  const [vendor, ...rest] = modelId.split(':');
  if (!vendor || rest.length === 0) return modelId;
  return `${vendor} · ${rest.join(':')}`;
}

/**
 * Format USD as JPY using the current rate. Pass a fallback if rate is missing
 * so the UI never shows weird values when /api/stats hasn't replied yet.
 */
function fmtJpy(usd: number, rate: number | undefined): string {
  const r = rate ?? 150;
  const jpy = usd * r;
  if (jpy < 1) return `¥${jpy.toFixed(2)}`;
  if (jpy < 10) return `¥${jpy.toFixed(1)}`;
  return `¥${Math.round(jpy).toLocaleString('en-US')}`;
}

function fmtPct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

function fmtTime(ts: number): string {
  return new Date(ts).toLocaleString('vi-VN', {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function truncate(s: string | null, n = 80): string {
  if (!s) return '—';
  return s.length > n ? s.slice(0, n) + '…' : s;
}

export function Dashboard({ auth }: DashboardProps) {
  const token = auth.token;
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [calls, setCalls] = useState<CallRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [endpointFilter, setEndpointFilter] = useState<string>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const jpyRate = stats?.jpy_rate;

  useEffect(() => {
    Promise.all([getStats(token, days), listCalls(token, { limit: 100, endpoint: endpointFilter || undefined })])
      .then(([s, c]) => {
        setStats(s);
        setCalls(c.calls);
      })
      .catch((err: Error) => setError(err.message));
  }, [token, days, endpointFilter]);

  return (
    <div className="min-h-screen bg-bg text-white">
      <header className="border-b border-border px-6 py-4 flex items-center justify-between sticky top-0 bg-bg/95 backdrop-blur z-10">
        <div>
          <h1 className="text-lg font-semibold">KRA Tools Admin</h1>
          <p className="text-xs text-muted">
            Translate + reply analytics · last {days} days
            {jpyRate && (
              <>
                {' · '}
                <span title={`Updated ${stats?.jpy_rate_fetched_at ?? '?'}`}>
                  ¥{jpyRate.toFixed(2)}/$
                </span>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-card border border-border rounded-lg px-3 py-1.5 text-sm"
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
          <span className="text-xs text-muted">
            {auth.name} {auth.role && <span className="text-accent">· {auth.role}</span>}
          </span>
          {auth.token && (
            <button
              onClick={auth.logout}
              className="text-xs text-muted hover:text-white border border-border rounded-lg px-3 py-1.5"
            >
              Logout
            </button>
          )}
        </div>
      </header>

      <main className="p-6 max-w-6xl mx-auto space-y-6">
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Overview cards */}
        {stats && (
          <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card label="Total calls" value={stats.overview.total_calls.toLocaleString()} />
            <Card label="Total cost" value={fmtJpy(stats.overview.total_cost_usd, jpyRate)} accent />
            <Card
              label="Sent as-is rate"
              value={fmtPct(stats.overview.sent_as_is_rate)}
              hint="Replies sent without edits"
            />
            <Card
              label="Endpoints used"
              value={String(stats.overview.by_endpoint.length)}
              hint={stats.overview.by_endpoint
                .slice(0, 3)
                .map((e) => `${ENDPOINT_LABELS[e.endpoint] ?? e.endpoint} (${e.calls})`)
                .join(' · ')}
            />
          </section>
        )}

        {/* By-endpoint breakdown */}
        {stats && stats.overview.by_endpoint.length > 0 && (
          <section className="bg-card rounded-2xl border border-border p-5">
            <h2 className="text-sm font-semibold mb-3">Cost by endpoint</h2>
            <div className="space-y-2">
              {stats.overview.by_endpoint.map((e) => (
                <div key={e.endpoint} className="flex items-center justify-between text-sm py-1">
                  <span className="text-muted">{ENDPOINT_LABELS[e.endpoint] ?? e.endpoint}</span>
                  <span>
                    <span className="text-muted mr-3">{e.calls.toLocaleString()} calls</span>
                    <span className="font-mono">{fmtJpy(e.cost_usd, jpyRate)}</span>
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Model config — live AI model selection per endpoint */}
        <ModelConfigSection token={token} onError={setError} />

        {/* ⚡ OpenAI Priority switch + speed per tier */}
        <ReplySpeedSection token={token} onError={setError} />

        {/* Brand context — facts AI knows when generating replies */}
        <BrandContextSection token={token} onError={setError} />

        {/* Who may call the Shortcut endpoints — staff-code lock */}
        <ShortcutLockSection token={token} onError={setError} />

        {/* OCR noise that still reaches the AI — anh marks it, applied live */}
        <NoiseReviewSection token={token} onOpenCall={setSelectedId} onError={setError} />

        {/* Daily trend (lightweight bar viz, no chart lib) */}
        {stats && stats.daily.length > 0 && (
          <section className="bg-card rounded-2xl border border-border p-5">
            <h2 className="text-sm font-semibold mb-3">Daily cost trend</h2>
            <DailyBars daily={stats.daily} jpyRate={jpyRate} />
          </section>
        )}

        {/* Recent calls */}
        <section className="bg-card rounded-2xl border border-border overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <h2 className="text-sm font-semibold">Recent calls</h2>
            <select
              value={endpointFilter}
              onChange={(e) => setEndpointFilter(e.target.value)}
              className="bg-card2 border border-border rounded-lg px-3 py-1.5 text-xs"
            >
              <option value="">All endpoints</option>
              <option value="reply-polish">Reply polish</option>
              <option value="reply-auto">Reply auto</option>
              <option value="translate">Translate</option>
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-card2 text-muted text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">When</th>
                  <th className="text-left px-4 py-2.5 font-medium">Endpoint</th>
                  <th className="text-left px-4 py-2.5 font-medium">Customer / msg</th>
                  <th className="text-left px-4 py-2.5 font-medium">Lang</th>
                  <th className="text-right px-4 py-2.5 font-medium">Tokens</th>
                  <th className="text-right px-4 py-2.5 font-medium">Cost</th>
                  <th className="text-left px-4 py-2.5 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {calls?.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => setSelectedId(c.id)}
                    className="border-t border-border hover:bg-card2/50 cursor-pointer"
                  >
                    <td className="px-4 py-2.5 text-muted">{fmtTime(c.ts)}</td>
                    <td className="px-4 py-2.5">
                      <span className="text-xs bg-card2 border border-border rounded px-2 py-0.5">
                        {ENDPOINT_LABELS[c.endpoint] ?? c.endpoint}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      {c.customer_id && (
                        <div className="text-xs text-accent mb-0.5">{c.customer_id}</div>
                      )}
                      <div className="text-muted">{truncate(c.customer_msg)}</div>
                    </td>
                    <td className="px-4 py-2.5">{c.detected_lang ?? '—'}</td>
                    <td className="px-4 py-2.5 text-right text-muted">
                      {c.input_tokens + c.output_tokens > 0
                        ? `${c.input_tokens}/${c.output_tokens}`
                        : '—'}
                      {!!c.cached_tokens && (
                        <div
                          className="text-xs text-green-400"
                          title="Input tokens served from the OpenAI prompt cache (billed ~10×cheaper)"
                        >
                          ⚡ {Math.round((c.cached_tokens / c.input_tokens) * 100)}% cache
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono">{fmtJpy(c.cost_usd, jpyRate)}</td>
                    <td className="px-4 py-2.5">
                      <span
                        className={
                          c.status === 'ok'
                            ? 'text-green-400'
                            : c.status === 'error'
                            ? 'text-red-400'
                            : 'text-yellow-400'
                        }
                      >
                        {c.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {calls?.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-muted">
                      No calls yet — make some via iPhone Shortcut to see them here.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {selectedId && (
        <CallDetailModal
          token={token}
          callId={selectedId}
          jpyRate={jpyRate}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

function Card({ label, value, hint, accent }: { label: string; value: string; hint?: string; accent?: boolean }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-4">
      <div className="text-xs text-muted">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${accent ? 'text-accent' : ''}`}>{value}</div>
      {hint && <div className="text-xs text-muted mt-1 truncate">{hint}</div>}
    </div>
  );
}

function DailyBars({
  daily,
  jpyRate,
}: {
  daily: StatsResponse['daily'];
  jpyRate: number | undefined;
}) {
  if (daily.length === 0) return <p className="text-muted text-sm">No data</p>;
  const max = Math.max(...daily.map((d) => d.total_cost_usd), 0.0001);
  const sorted = [...daily].sort((a, b) => a.day.localeCompare(b.day));
  return (
    <div className="flex items-end gap-1 h-32">
      {sorted.map((d) => {
        const h = (d.total_cost_usd / max) * 100;
        return (
          <div
            key={d.day}
            className="flex-1 bg-accent/60 hover:bg-accent rounded-t transition-colors group relative cursor-pointer"
            style={{ height: `${Math.max(2, h)}%` }}
            title={`${d.day} · ${d.calls} calls · ${fmtJpy(d.total_cost_usd, jpyRate)}`}
          >
            <div className="opacity-0 group-hover:opacity-100 absolute -top-12 left-1/2 -translate-x-1/2 text-xs bg-card2 border border-border rounded px-2 py-1 whitespace-nowrap pointer-events-none transition-opacity">
              {d.day} · {d.calls} · {fmtJpy(d.total_cost_usd, jpyRate)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CallDetailModal({
  token,
  callId,
  jpyRate,
  onClose,
}: {
  token: string | null;
  callId: string;
  jpyRate: number | undefined;
  onClose: () => void;
}) {
  const [data, setData] = useState<CallDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getCallDetail(token, callId)
      .then(setData)
      .catch((err: Error) => setError(err.message));
  }, [token, callId]);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-border px-5 py-4 flex items-center justify-between">
          <h2 className="font-semibold">Call detail</h2>
          <button onClick={onClose} className="text-muted hover:text-white">
            ✕
          </button>
        </div>

        {error && <div className="p-5 text-red-400">{error}</div>}

        {data && (
          <div className="p-5 space-y-4 text-sm">
            <Field label="ID" value={data.call.id} mono />
            <Field label="Endpoint" value={data.call.endpoint} />
            <Field label="Model" value={data.call.model} />
            <Field
              label="Tokens / cost"
              value={`${data.call.input_tokens} in${data.call.cached_tokens ? ` (${data.call.cached_tokens} từ cache)` : ''} · ${data.call.output_tokens} out · ${fmtJpy(data.call.cost_usd, jpyRate)}`}
            />
            <Field label="Latency" value={`${data.call.latency_ms} ms`} />
            <Field label="Customer" value={data.call.customer_id ?? '—'} />
            <Field label="Platform" value={data.call.platform ?? '—'} />
            <Field
              label="Detected"
              value={`${data.call.detected_lang ?? '—'} · ${data.call.detected_tone ?? '—'}`}
            />

            {data.lines && data.lines.length > 0 ? (
              <AiSeesView token={token} lines={data.lines} onError={setError} />
            ) : data.call.customer_msg && (
              <div>
                <div className="text-xs text-muted mb-1">Customer message</div>
                <div className="bg-card2 border border-border rounded-xl p-3 whitespace-pre-wrap">
                  {data.call.customer_msg}
                </div>
              </div>
            )}

            {data.call.draft && (
              <div>
                <div className="text-xs text-muted mb-1">Owner draft</div>
                <div className="bg-card2 border border-border rounded-xl p-3 whitespace-pre-wrap">
                  {data.call.draft}
                </div>
              </div>
            )}

            {data.variants.length > 0 && (
              <div>
                <div className="text-xs text-muted mb-2">Reply variants ({data.variants.length})</div>
                <div className="space-y-2">
                  {data.variants.map((v) => (
                    <div key={v.id} className="bg-card2 border border-border rounded-xl p-3">
                      <div className="text-xs text-muted mb-1">Variant {v.idx + 1}</div>
                      <div className="whitespace-pre-wrap">{v.text}</div>
                      {v.vi_lit && (
                        <div className="mt-2 pt-2 border-t border-border text-xs text-muted">
                          <span className="text-accent">VI:</span> {v.vi_lit}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.call.error_msg && (
              <div>
                <div className="text-xs text-red-400 mb-1">Error</div>
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-red-400">
                  {data.call.error_msg}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ModelConfigSection({
  token,
  onError,
}: {
  token: string | null;
  onError: (msg: string) => void;
}) {
  const [config, setConfig] = useState<ModelConfigResponse | null>(null);
  const [savingFor, setSavingFor] = useState<ConfigEndpoint | null>(null);
  const [justSavedFor, setJustSavedFor] = useState<ConfigEndpoint | null>(null);

  useEffect(() => {
    getModelConfig(token)
      .then(setConfig)
      .catch((err: Error) => onError(err.message));
  }, [token, onError]);

  async function handleChange(endpoint: ConfigEndpoint, modelId: string) {
    if (!config || config.current[endpoint] === modelId) return;
    setSavingFor(endpoint);
    try {
      await setModelConfig(token, endpoint, modelId);
      setConfig({ ...config, current: { ...config.current, [endpoint]: modelId } });
      setJustSavedFor(endpoint);
      setTimeout(() => setJustSavedFor((cur) => (cur === endpoint ? null : cur)), 1500);
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingFor(null);
    }
  }

  if (!config) return null;

  return (
    <section className="bg-card rounded-2xl border border-border p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold">AI model per endpoint</h2>
        <span className="text-xs text-muted">Đổi xong là ăn ngay — không cần redeploy</span>
      </div>
      <div className="space-y-3">
        {config.endpoints.map((ep) => {
          const current = config.current[ep];
          const allowed = config.allowed[ep] ?? [];
          const labels = CONFIG_ENDPOINT_LABELS[ep];
          return (
            <div
              key={ep}
              className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 border-t border-border pt-3 first:border-t-0 first:pt-0"
            >
              <div className="min-w-0">
                <div className="text-sm">{labels.title}</div>
                <div className="text-xs text-muted">{labels.hint}</div>
              </div>
              <div className="flex items-center gap-2">
                {justSavedFor === ep && (
                  <span className="text-xs text-green-400">✓ saved</span>
                )}
                <select
                  value={current}
                  disabled={savingFor === ep}
                  onChange={(e) => handleChange(ep, e.target.value)}
                  className="bg-card2 border border-border rounded-lg px-3 py-1.5 text-xs font-mono min-w-[16rem] disabled:opacity-50"
                >
                  {allowed.map((m) => (
                    <option key={m} value={m}>
                      {fmtModel(m)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function BrandContextSection({
  token,
  onError,
}: {
  token: string | null;
  onError: (msg: string) => void;
}) {
  const [config, setConfig] = useState<BrandContextResponse | null>(null);
  const [draft, setDraft] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    getBrandContext(token)
      .then((c) => {
        setConfig(c);
        setDraft(c.using_default ? c.default_content : c.content);
      })
      .catch((err: Error) => onError(err.message));
  }, [token, onError]);

  if (!config) return null;

  const current = config.using_default ? config.default_content : config.content;
  const dirty = draft !== current;
  const overLimit = draft.length > config.max_chars;

  async function handleSave() {
    if (overLimit) return;
    setSaving(true);
    try {
      // Empty string → server stores '' → next read falls back to default.
      await setBrandContext(token, draft.trim());
      const next = await getBrandContext(token);
      setConfig(next);
      setDraft(next.using_default ? next.default_content : next.content);
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt((cur) => (cur && Date.now() - cur >= 1500 ? null : cur)), 1600);
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  function handleReset() {
    setDraft(config!.default_content);
  }

  return (
    <section className="bg-card rounded-2xl border border-border p-5">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold">Brand context (facts AI sees)</h2>
        <span className="text-xs text-muted">
          {config.using_default
            ? 'Đang dùng default (chưa custom)'
            : config.updated_at
            ? `Lưu lần cuối: ${new Date(config.updated_at).toLocaleString('vi-VN')}`
            : ''}
        </span>
      </div>
      <p className="text-xs text-muted mb-3">
        Nội dung này được nhét lên đầu mọi prompt Reply (polish + auto). Sửa xong bấm Save là call
        kế tiếp ăn ngay — không cần redeploy. Để trống + Save = trở về default.
      </p>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        spellCheck={false}
        className="w-full bg-card2 border border-border rounded-xl px-3 py-2 text-sm font-mono leading-relaxed min-h-[280px] resize-y focus:outline-none focus:border-accent/60"
      />
      <div className="flex items-center justify-between mt-2">
        <div className="text-xs text-muted">
          <span className={overLimit ? 'text-red-400' : ''}>
            {draft.length.toLocaleString()} / {config.max_chars.toLocaleString()} chars
          </span>
        </div>
        <div className="flex items-center gap-2">
          {savedAt && <span className="text-xs text-green-400">✓ saved</span>}
          <button
            onClick={handleReset}
            disabled={saving || draft === config.default_content}
            className="text-xs text-muted hover:text-white border border-border rounded-lg px-3 py-1.5 disabled:opacity-40 disabled:hover:text-muted"
          >
            Reset to default
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !dirty || overLimit}
            className="text-xs bg-accent/80 hover:bg-accent text-white rounded-lg px-4 py-1.5 disabled:opacity-40 disabled:hover:bg-accent/80"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted text-xs uppercase tracking-wide">{label}</span>
      <span className={`text-right ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
    </div>
  );
}
